import { useState } from 'react';
import type { FormEvent } from 'react';

import { Avviso, Campo, Pulsante } from '../../componenti';
import { ErroreApi, ErroreRete } from '../../lib/api';
import { ora, oreFra } from '../../lib/formato';
import { useSessione } from '../../stato/Sessione';
import { Marchio, useIntestazione } from './Marchio';
import { UtenzaDisattivata, comeDisattivazione } from './UtenzaDisattivata';
import type { Disattivazione } from './UtenzaDisattivata';

/**
 * 6i — sessione scaduta.
 *
 * Distinta dal blocco per inattività: qui il cookie è invalidato e serve un login completo,
 * con nome utente e password. Nessuno sblocco con la sola password.
 */

export function SessioneScaduta() {
  const intestazione = useIntestazione();
  const { entra, precedente } = useSessione();

  const [username, setUsername] = useState(precedente?.username ?? '');
  const [password, setPassword] = useState('');
  const [errore, setErrore] = useState<string | null>(null);
  const [disattivata, setDisattivata] = useState<Disattivazione | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const durata =
    precedente && oreFra(precedente.creata_il, precedente.scade_il);

  async function invia(e: FormEvent) {
    e.preventDefault();
    if (inCorso) return;
    setInCorso(true);
    setErrore(null);
    try {
      await entra(username.trim(), password);
    } catch (err) {
      // L'utenza può essere stata disattivata proprio mentre la sessione era aperta: anche
      // da qui si deve poter arrivare alla 6e.
      const spenta = comeDisattivazione(err);
      if (spenta) setDisattivata(spenta);
      else if (err instanceof ErroreRete) setErrore('Serve la connessione. Riprova.');
      else if (err instanceof ErroreApi) setErrore(err.message);
      else throw err;
      setPassword('');
      setInCorso(false);
    }
  }

  if (disattivata) {
    return <UtenzaDisattivata dati={disattivata} onTorna={() => setDisattivata(null)} />;
  }

  return (
    <div className="accesso accesso--colonna" data-densita="tattile">
      <Marchio intestazione={intestazione} />

      <div className="accesso__blocco-avviso">
        <div className="accesso__blocco-titolo">La sessione è scaduta</div>
        <div className="accesso__blocco-testo">
          {precedente
            ? `Sono passate ${durata} ore dall’accesso delle ${ora(precedente.creata_il)}. Per sicurezza serve un nuovo login completo, con nome utente e password.`
            : 'Per sicurezza serve un nuovo login completo, con nome utente e password.'}
        </div>
      </div>

      {errore ? <Avviso tono="errore" titolo={errore} /> : null}

      <form className="accesso__scheda" onSubmit={invia}>
        <Campo
          etichetta="Nome utente"
          autoComplete="username"
          autoCapitalize="none"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <Campo
          etichetta="Password"
          type="password"
          autoComplete="current-password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Pulsante type="submit" largo grande disabled={inCorso}>
          Entra di nuovo
        </Pulsante>
      </form>

      <div className="accesso__nota">
        Il QR eventualmente aperto non è più valido: rigeneralo dopo l’accesso.
      </div>
    </div>
  );
}
